import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  User,
  Building,
  Shield,
  Bell,
  Link,
  Key,
  Download,
  Settings as SettingsIcon
} from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ProfileSettings } from './settings/ProfileSettings'
import { OrganizationSettings } from './settings/OrganizationSettings'
import { FraudSettings } from './settings/FraudSettings'
import { NotificationSettings } from './settings/NotificationSettings'
import { IntegrationSettings } from './settings/IntegrationSettings'
import { SecuritySettings } from './settings/SecuritySettings'
import { ExportSettings } from './settings/ExportSettings'

export function Settings() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'profile'
  
  const tabs = [
    { id: 'profile', label: 'Profile', icon: User, component: ProfileSettings },
    { id: 'organization', label: 'Organization', icon: Building, component: OrganizationSettings },
    { id: 'fraud', label: 'Fraud Detection', icon: Shield, component: FraudSettings },
    { id: 'notifications', label: 'Notifications', icon: Bell, component: NotificationSettings },
    { id: 'integrations', label: 'Integrations', icon: Link, component: IntegrationSettings },
    { id: 'security', label: 'Security', icon: Key, component: SecuritySettings },
    { id: 'export', label: 'Export & Backup', icon: Download, component: ExportSettings }
  ]
  
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value })
  }
  
  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage your account settings and preferences
        </p>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <TabsList className="w-full h-auto p-0 bg-transparent">
              <div className="flex overflow-x-auto">
                {tabs.map(tab => (
                  <TabsTrigger 
                    key={tab.id} 
                    value={tab.id}
                    className="flex items-center gap-2 px-6 py-4 border-b-2 border-transparent data-[state=active]:border-primary-500 data-[state=active]:text-primary-600 dark:data-[state=active]:text-primary-400 whitespace-nowrap"
                  >
                    <tab.icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </TabsTrigger>
                ))}
              </div>
            </TabsList>
          </div>
          
          <div className="p-6">
            {tabs.map(tab => (
              <TabsContent key={tab.id} value={tab.id} className="mt-0">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <tab.component />
                </motion.div>
              </TabsContent>
            ))}
          </div>
        </Tabs>
      </div>
    </div>
  )
}